/**
 * Progress — duplex v3 §4: a short spoken line for a MAIN tool call ("checking the board…") so a long tool run is not
 * dead air. A per-tool template table first (0ms, per language); else the fast model writes 2-5 words from the tool's
 * NAME and a short args hint only — never the history. A line never states a result (RESULT_CLAIM_RE), never repeats,
 * and none follows once MAIN's text has streamed this turn. The caller (VoiceCall) owns timing: speak only after ≥3s of
 * silence, at most one per `gapMs`. Lines go to the host's speak/show only — never to MAIN's history.
 */
import { forComponent } from '../logging';
import type { ChatLike, ChatResponse } from '../llm';
import { cleanLine, languageName, norm, personaHint, type Gender } from './channelLang';
import { RESULT_CLAIM_RE } from './leak';

const log = forComponent('Progress');
/** Hebrew result claims ("מצאתי", "סיימתי", "אין …") — RESULT_CLAIM_RE is English. */
const HE_RESULT_CLAIM = /(?:^|\s)(?:מצאתי|סיימתי|בוצע|הנה|אין)(?=\s|$|[.,!…])/u;

type Topic = 'board' | 'websearch' | 'page' | 'browser' | 'search' | 'mail' | 'calendar' | 'memory' | 'write' | 'run' | 'read' | 'delegate';
/** Tool name → topic (first match wins; names are lowercased, MCP prefixes included). A web SEARCH is not reading a
 *  page: WebSearch matched the page topic and a call said "reading the page…" while it searched (bare-browser #938). */
const TOPICS: [RegExp, Topic | null][] = [
  [/toolsearch|^mcp__.*__(list_commands|call_info)$/, null], // tool plumbing — no line (and no generated one)
  [/kanban|board|card|\bkb\b|list_cards/, 'board'],
  [/mail|gmail|inbox/, 'mail'],
  [/calendar|event|schedule/, 'calendar'],
  [/memory|recall|remember/, 'memory'],
  [/web_?search|search_web|google/, 'websearch'],
  [/fetch|read_page|get_page|page_text|navigate|url|history/, 'page'],
  [/click|type|press_key|scroll|screenshot|tab|browser|highlight|point|wait_for|upload/, 'browser'],
  [/grep|glob|search|find|lookup|quicklook/, 'search'],
  [/^(agent|task)$|subagent|delegate/, 'delegate'],
  [/edit|write|patch|create|update|save|delete|move/, 'write'],
  [/shell|bash|exec|run|command|terminal|eval/, 'run'],
  [/read|open|view|cat|look|get|list|ls/, 'read'],
];
/** Two phrasings per topic and language (never the same line twice in a call). Hebrew is gendered on the speaker:
 *  'he-f' / 'he-m' follow the voice (ProgressOptions.gender); 'he' (plural present) when the gender is unknown. */
export const PROGRESS_TEMPLATES: Record<string, Record<Topic, string[]>> = {
  en: {
    board: ['checking the board…', 'looking at the cards…'], mail: ['checking the mail…', 'going through the inbox…'],
    calendar: ['checking the calendar…', 'looking at the schedule…'], memory: ['checking my notes…', 'looking back…'],
    websearch: ['searching…', 'searching the web…'], page: ['reading the page…', 'going through the page…'],
    browser: ['working in the browser…', 'on the page now…'], search: ['searching…', 'still looking…'],
    delegate: ['working on it…', 'still on it…'],
    write: ['making the change…', 'writing it down…'], run: ['running it…', 'still running…'], read: ['reading it…', 'going through it…'],
  },
  he: {
    board: ['בודקים בלוח…', 'מסתכלים על הכרטיסים…'], mail: ['בודקים את המייל…', 'עוברים על הדואר…'],
    calendar: ['בודקים ביומן…', 'מסתכלים בלו״ז…'], memory: ['בודקים ברשימות…', 'מחפשים בזיכרון…'],
    websearch: ['מחפשים…', 'מחפשים ברשת…'], page: ['קוראים את הדף…', 'עוברים על הדף…'],
    browser: ['עובדים בדפדפן…', 'על הדף עכשיו…'], search: ['מחפשים…', 'עדיין מחפשים…'],
    delegate: ['עובדים על זה…', 'עדיין על זה…'],
    write: ['מבצעים את השינוי…', 'כותבים את זה…'], run: ['מריצים…', 'עדיין רץ…'], read: ['קוראים…', 'עוברים על זה…'],
  },
  'he-f': {
    board: ['בודקת בלוח…', 'מסתכלת על הכרטיסים…'], mail: ['בודקת את המייל…', 'עוברת על הדואר…'],
    calendar: ['בודקת ביומן…', 'מסתכלת בלו״ז…'], memory: ['בודקת ברשימות…', 'מחפשת בזיכרון…'],
    websearch: ['מחפשת…', 'מחפשת ברשת…'], page: ['קוראת את הדף…', 'עוברת על הדף…'],
    browser: ['עובדת בדפדפן…', 'על הדף עכשיו…'], search: ['מחפשת…', 'עדיין מחפשת…'],
    delegate: ['עובדת על זה…', 'עדיין על זה…'],
    write: ['מבצעת את השינוי…', 'כותבת את זה…'], run: ['מריצה…', 'עדיין רץ…'], read: ['קוראת…', 'עוברת על זה…'],
  },
  'he-m': {
    board: ['בודק בלוח…', 'מסתכל על הכרטיסים…'], mail: ['בודק את המייל…', 'עובר על הדואר…'],
    calendar: ['בודק ביומן…', 'מסתכל בלו״ז…'], memory: ['בודק ברשימות…', 'מחפש בזיכרון…'],
    websearch: ['מחפש…', 'מחפש ברשת…'], page: ['קורא את הדף…', 'עובר על הדף…'],
    browser: ['עובד בדפדפן…', 'על הדף עכשיו…'], search: ['מחפש…', 'עדיין מחפש…'],
    delegate: ['עובד על זה…', 'עדיין על זה…'],
    write: ['מבצע את השינוי…', 'כותב את זה…'], run: ['מריץ…', 'עדיין רץ…'], read: ['קורא…', 'עובר על זה…'],
  },
};

export class ProgressOptions {
  templates: Record<string, Partial<Record<Topic, string[]>>> = PROGRESS_TEMPLATES;
  /** The voice's gender: picks the gendered table (`${lang}-f`/`-m`) when one exists. */
  gender: Gender = '';
  /** Fast-model fallback for a tool with no template (fastProgressGenerator). Absent → silence. */
  generate?: (tool: string, hint: string, lang: string, signal: AbortSignal) => Promise<string>;
  generateBudgetMs = 800;
  /** Speak only when nothing has been audible for this long. */
  silenceMs = 3000;
  /** At most one progress line per this long. */
  gapMs = 4000;
  now: () => number = () => performance.now();
}

/** A short hint of what a tool call is about: its first short string argument. */
export function argsHint(args: unknown): string {
  if (!args || typeof args !== 'object') return typeof args === 'string' ? args.slice(0, 40) : '';
  const s = Object.values(args as Record<string, unknown>).find((v) => typeof v === 'string' && v.trim());
  return typeof s === 'string' ? s.trim().replace(/\s+/g, ' ').slice(0, 40) : '';
}

export class Progress {
  private said = new Set<string>();
  private lastAt = -Infinity;
  private streamed = false;

  public constructor(public options?: Partial<ProgressOptions>) {
    this.options = { ...new ProgressOptions(), ...options };
  }
  private get o(): ProgressOptions { return this.options as ProgressOptions; }

  /** A new MAIN turn started. */
  turn(): void { this.streamed = false; }
  /** MAIN's text streamed this turn: no more progress lines for it. */
  text(): void { this.streamed = true; }
  /** May a line play now? (the caller passes when the user last heard anything) */
  due(lastAudibleAt: number): boolean {
    const now = this.o.now();
    return !this.streamed && now - lastAudibleAt >= this.o.silenceMs && now - this.lastAt >= this.o.gapMs;
  }
  /** Mark `line` spoken. */
  spoke(line: string): void { this.said.add(norm(line)); this.lastAt = this.o.now(); }

  /** The line for this tool call in `lang`, or '' (no template and no/late/invalid generation, a repeat, a result claim). */
  async line(tool: string, args: unknown, lang: string): Promise<string> {
    if (this.streamed) return '';
    const name = tool.toLowerCase();
    const hit = TOPICS.find(([re]) => re.test(name));
    if (hit && hit[1] === null) return '';
    const topic = hit?.[1];
    const g = this.o.gender ? this.o.templates[`${lang}-${this.o.gender[0]}`] : undefined;
    const pool = topic ? (g ?? this.o.templates[lang])?.[topic] ?? [] : [];
    const fresh = pool.find((l) => !this.said.has(norm(l)));
    if (fresh) return fresh;
    if (!this.o.generate) return '';
    const ac = new AbortController();
    let t: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<null>((r) => { t = setTimeout(() => { ac.abort(); r(null); }, this.o.generateBudgetMs); });
    const raw = await Promise.race([this.o.generate(tool, argsHint(args), lang, ac.signal).catch((e) => { log.warn(`progress line failed: ${(e as Error)?.message ?? e}`); return null; }), late]).finally(() => clearTimeout(t));
    const l = cleanLine(raw ?? '', lang, 5);
    if (!l || RESULT_CLAIM_RE.test(l) || HE_RESULT_CLAIM.test(l) || this.said.has(norm(l)) || this.streamed) return '';
    return l;
  }
}

/** Fast-model progress writer: tool name + args hint only, no history. */
export function fastProgressGenerator(ai: ChatLike, model: string, gender?: Gender): NonNullable<ProgressOptions['generate']> {
  return async (tool, hint, lang, signal) => {
    const prompt = `An assistant on a live voice call is running the tool "${tool}"${hint ? ` (about: "${hint}")` : ''}. ` +
      `Write ONE spoken line of 2-5 words, in ${languageName(lang)}, saying what it is doing right now (like "checking the board…"). ` +
      `Never state a result or a finding. ${personaHint(gender)} Output only the line.`;
    const reasoning = /gpt-oss/.test(model) ? { providerOptions: { reasoning_effort: 'low' } } : {};
    const r = (await ai.chat({ model, messages: [{ role: 'user', content: prompt }], stream: false, signal, maxTokens: 150, ...reasoning })) as ChatResponse;
    return r.content ?? '';
  };
}
